import { Button, Card, CardBody, Col, Input, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, Spinner, TabContent, TabPane } from 'reactstrap';
import { AlertCircle, Calendar, Check, CheckCircle, Heart, Send, X, XCircle } from 'react-feather';
import { useMutation, useQuery } from 'react-query';
import { emailVaccinations, getClient, getPatientMedications, getPatientVaccinations, refillMedication } from '../../../../../apis/customer.api';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import userState from '../../../../../recoil/atoms/auth/user';
import shopState from '../../../../../recoil/atoms/current-shop';
import useJwt from '@src/@core/auth/jwt/useJwt';
import NavbarSms from '../../../../../@core/layouts/components/navbar/NavbarSms';
import { getSpecies } from '../../../../../apis';
import '@src/@core/scss/react/pages/page-profile.scss';
import toast from '@components/toast';

const MyPets = () => {
  const [shop] = useRecoilState(shopState);
  const [user] = useRecoilState(userState);
  const { jwt } = useJwt();
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [activePatientId, setActivePatientId] = useState(null);
  const [vaccinations, setVaccinations] = useState([]);
  const [medications, setMedications] = useState([]);
  const [species, setSpecies] = useState([]);
  const [tab, setTab] = useState('vaccinations');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [errors, setErrors] = useState({
    email: null,
  });
  const [email, setEmail] = useState('');
  const [refillModalOpen, setRefillModalOpen] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [refillMessage, setRefillMessage] = useState('');
  const [loadingSendEmail, setLoadingSendEmail] = useState(false);

  const bookAppointment = () => {
    const clientId = jwt.getClientId();
    window.location.href = `/${clientId}`;
  };

  const { data: _client, isFetchingClient, refetch: refetchClient, isLoading: isLoadingClient } = useQuery(
    ['client', user?.id], () => getClient({ user_id: user?.id }),
    {
      onSuccess: (data) => {
        setClient(data?.data);
        const tmp = data?.data?.patients?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        setPatients(tmp || []);
        if (tmp?.length === 1) {
          setActivePatientId(tmp[0].id);
        }
        setIsLoading(false);
      },
      onError: (error) => {
        setIsLoading(false);
      },
      enabled: !!user?.id,
      refetchOnWindowFocus: true
    }
  );

  useEffect(() => {
    if (!activePatientId) {
      setVaccinations([]);
      setMedications([]);
    }
  }, [activePatientId]);

  const { data: _vaccinations, isFetchingVaccinations, refetch: refetchVaccinations, isLoading: isLoadingVaccinations } = useQuery(
    ['patientVaccinations', activePatientId], () => getPatientVaccinations({ patient_id: activePatientId }),
    {
      onSuccess: (data) => {
        setVaccinations(data?.data || []);
        setIsLoading(false);
      },
      onError: (error) => {
        setIsLoading(false);
      },
      enabled: !!activePatientId
    }
  );

  const { data: _medications, isFetchingMedications, refetch: refetchMedications, isLoading: isLoadingMedications } = useQuery(
    ['patientMedications', activePatientId], () => getPatientMedications({ patient_id: activePatientId }),
    {
      onSuccess: (data) => {
        setMedications(data?.data || []);
        setIsLoading(false);
      },
      onError: (error) => {
        setIsLoading(false);
      },
      enabled: !!activePatientId
    }
  );

  useQuery(['getSpecies'], () => {
    return getSpecies({ page: 0, size: 1000 })
  }, {
    onSuccess: ({ data }) => {
      if (data?.data) {
        setSpecies(data.data);
      }
    }
  });

  const speciesFromId = id => {
    return species?.find(item => item.id === id)?.name;
  };

  const getSpeciesEmoji = speciesId => {
    const species = speciesFromId(speciesId);
    if (species) {
      if (species === 'Bird') {
        return '🐦';
      }
      if (species === 'Cat') {
        return '🐈';
      }
      if (species === 'Dog') {
        return '🐶';
      }
      if (species === 'Equine') {
        return '🐴';
      }
      if (species === 'Ferret') {
        return '🐨';
      }
      if (species === 'Fish') {
        return '🐟';
      }
      if (species === 'Guinea Pig') {
        return '🐷';
      }
      if (species === 'Rabbit') {
        return '🐰';
      }
      if (species === 'Reptile') {
        return '🦎';
      }
    }
    return '❤️';
  };

  const scheduleNow = () => {
    const clientId = jwt.getClientId();
    window.location.href = `/${clientId}`;
  };

  const displayHeader = () => {
    if (client?.first_name) {
      return `Hello, ${client?.first_name}!`
    } else {
      return `Hello! Schedule an appointment${shop?.name ? ` with ${shop?.name}` : ''} to get started.`
    }
  };

  const onClickSendEmail = () => {
    setEmailModalOpen(true);
  };

  const onClickRequestRefill = (medication) => {
    setRefillModalOpen(true);
    setSelectedMedication(medication);
  };

  const emailRegExp = /^[\w.]+[\+]*[\w.]+@([\w-]+\.)+[\w-]{2,4}$/;
  const validateEmail = (email) => {
    if (email.endsWith('.oliver')) {
      return true;
    }
    return emailRegExp.test(email);
  };

  const onChangeEmail = (email) => {
    setEmail(email);
    setErrors({ ...errors, email: null });
  };

  const emailVaccinationsMutation = useMutation(emailVaccinations);

  const onSendEmail = () => {
    if (!validateEmail(email)) {
      setErrors({ ...errors, email: 'Please enter a valid email' });
      return;
    }
    setLoadingSendEmail(true);
    setErrors({ ...errors, email: null });
    emailVaccinationsMutation.mutate({ email, patient_id: activePatientId }, {
      onSuccess: () => {
        setEmailModalOpen(false);
        toast.success('Email sent');
        setEmail('');
        setLoadingSendEmail(false);
      },
      onError: () => {
        setEmailModalOpen(false);
        toast.error('Unable to send email');
        setEmail('');
        setLoadingSendEmail(false);
      }
    });
  };

  const requestRefillMutation = useMutation(refillMedication);

  const onRequestRefill = () => {
    setLoadingSendEmail(true);
    requestRefillMutation.mutate({ patient_id: activePatientId, medication_id: selectedMedication.id, note: refillMessage }, {
      onSuccess: () => {
        setRefillModalOpen(false);
        toast.success('Refill request sent!');
        setRefillMessage('');
        setSelectedMedication(null);
        setLoadingSendEmail(false);
      },
      onError: () => {
        setRefillModalOpen(false);
        toast.error('Unable to request refill');
        setRefillMessage('');
        setSelectedMedication(null);
        setLoadingSendEmail(false);
      }
    });
  };

  const formatExpireLabel = (date) => {
    if (!date) return 'Expiration:';

    const today = new Date();
    const expireDate = new Date(date);
    if (expireDate < today) {
      return 'Expired:';
    } else {
      return 'Expires:';
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    return [
      phoneNumber.slice(0, 3),
      phoneNumber.slice(3, 6),
      phoneNumber.slice(6)
    ].join('-');
  };

  const formatMedicationDetail = (medication) => {
    if (!medication) return '-';
    let result = '';
    result += `${medication.quantity} | `;
    if (medication.refills > 0) {
      result += `${medication.refills} refill${medication.refills > 1 ? 's' : ''}`;
    } else {
      result += 'no refills';
    }
    return result;
  };

  return (
    <Card style={{ borderRadius: '25px' }}>
      <CardBody className="px-0 px-md-2">
        <h4 className="" style={{ fontFamily: 'Spartan', fontWeight: 'bold' }}>My Pets at {shop?.name} <Heart className="text-danger" /></h4>
        <div className="mt-1 mb-3 pt-1">
          {!isLoading && !isLoadingClient ? (
            <>
              <h5>
                {displayHeader()}
              </h5>
              {!!client && patients.length > 0 && (
                <>
                  <div className='pets-header-row'>
                    {patients.map((patient, index) => (
                      <div key={patient.id} className="d-flex justify-content-center align-items-center flex-column mr-1">
                        <div
                          key={index}
                          id={`pet-${patient.id}`}
                          className={`pet-avatar ${activePatientId === patient.id ? 'pet-active' : ''}`}
                          onClick={() => setActivePatientId(patient.id)}
                        >
                          <p className='pet-emoji text-center'>{getSpeciesEmoji(patient?.species_id)}</p>
                        </div>
                        <p className='font-weight-bold pet-name'>{patient?.name}</p>
                      </div>
                    ))}
                  </div>
                  <div className='medical-history-row'>
                    <Nav tabs>
                      <NavItem>
                        <NavLink
                          active={tab === 'vaccinations'}
                          onClick={() => {
                            setTab('vaccinations');
                          }}
                        >
                          Vaccinations
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          active={tab === 'medications'}
                          onClick={() => {
                            setTab('medications');
                          }}
                        >
                          Medications
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent className="pt-1" activeTab={tab}>
                      <TabPane tabId="vaccinations">
                        <div className='pet-actions-row'>
                          {activePatientId && !!vaccinations.length && (
                            <Button
                              className='pet-actions-button'
                              color='primary'
                              outline
                              style={{ borderRadius: '15px' }}
                              onClick={onClickSendEmail}
                            >
                              Email Vaccination History<Send style={{ marginLeft: '5px' }} size={18} />
                            </Button>
                          )}
                          {activePatientId && (
                            <Button
                              className='pet-actions-button'
                              color='primary'
                              outline
                              style={{ borderRadius: '15px', marginLeft: '10px' }}
                              onClick={bookAppointment}
                            >
                              Book Appointment
                            </Button>
                          )}
                        </div>
                        <div className='vaccination-list'>
                          {vaccinations?.map((vaccination, index) => (
                            <div
                              key={`vaccination-item-${index}`}
                              className='vaccination-item'
                            >
                              <Col xs={10} className='px-0'>
                                <p className='font-weight-bolder'>{vaccination?.description}</p>
                                <span className='d-flex vaccination-date'>
                                  <p className='font-weight-bold mb-0'>Issued:</p>
                                  <p className='mb-0'>{formatDate(vaccination?.issue_date)}</p>
                                </span>
                                <span className='d-flex vaccination-date'>
                                  <p className={`mb-0 ${vaccination?.expired ? 'text-danger font-weight-bolder' : 'font-weight-bold'}`} style={{ color: vaccination?.expired ? '#EA6B63' : '' }}>{formatExpireLabel(vaccination?.expiration_date)}</p>
                                  <p className='mb-0'>{formatDate(vaccination?.expiration_date)}</p>
                                </span>
                              </Col>
                              <Col xs={2}>
                                {vaccination?.expired && (
                                  <p className='vaccination-checkmark checkmark-expired'><XCircle size={25} style={{ color: '#EA6B63' }} /></p>
                                )}
                                {!vaccination?.expired && vaccination?.expires_soon && (
                                <p className='vaccination-checkmark checkmark-expiring'><AlertCircle size={25} style={{ color: 'orange' }} /></p>
                                )}
                                {!vaccination?.expired && !vaccination?.expires_soon && (
                                  <p className='vaccination-checkmark checkmark-active'><CheckCircle size={25} color='rgb(67, 151, 61)' /></p>
                                )}
                              </Col>
                            </div>
                          ))}
                          {isLoadingVaccinations && (
                            <div className="d-flex justify-content-center mt-2 mb-2">
                              <Spinner />
                            </div>
                          )}
                          {!isLoadingVaccinations && vaccinations?.length === 0 && !!activePatientId && (
                            <p className='mt-2 text-center font-weight-bold'>Nothing to see here yet!</p>
                          )}
                          {!activePatientId && (
                            <p className='mt-2 text-center font-weight-bold'>Select a pet above to see details!</p>
                          )}
                        </div>
                      </TabPane>
                      <TabPane tabId="medications">
                      <div className='medication-list'>
                          {medications?.map((medication, index) => (
                            <div
                              key={`medication-item-${index}`}
                              className='vaccination-item'
                            >
                              <Col xs={8} className='px-0'>
                                <p className='font-weight-bolder'>{medication?.description}</p>
                                <span className='d-flex vaccination-date'>
                                  <p className='font-weight-bold mb-0'>Issued:</p>
                                  <p className='mb-0'>{formatDate(medication?.issue_date)}</p>
                                </span>
                                <span className='d-flex vaccination-date'>
                                  <span className='mb-0'><span className='mb-0 font-weight-bold'>Instructions:</span> {medication?.instructions}</span>
                                </span>
                              </Col>
                              <Col xs={4} className='align-self-center'>
                                <Button
                                  className='pet-actions-button'
                                  key={`medication-refill-button-${index}`}
                                  color='primary'
                                  outline
                                  style={{ borderRadius: '15px' }}
                                  onClick={() => onClickRequestRefill(medication)}
                                >
                                  Request Refill
                                </Button>
                              </Col>
                            </div>
                          ))}
                          {isLoadingMedications && (
                            <div className="d-flex justify-content-center mt-2 mb-2">
                              <Spinner />
                            </div>
                          )}
                          {!isLoadingMedications && medications?.length === 0 && !!activePatientId && (
                            <p className='mt-2 text-center font-weight-bold'>Nothing to see here yet!</p>
                          )}
                          {!activePatientId && (
                            <p className='mt-2 text-center font-weight-bold'>Select a pet above to see details!</p>
                          )}
                        </div>
                      </TabPane>
                    </TabContent>
                    <Modal
                      isOpen={emailModalOpen}
                      className="modal-dialog-centered"
                      toggle={() => setEmailModalOpen(!emailModalOpen)}
                    >
                      <ModalHeader style={{ borderRadius: '25px' }}>
                        Email Vaccination History
                      </ModalHeader>
                      <ModalBody>
                        <p style={{ fontSize: '12px' }}>Enter an email address to send {patients.find(p => p.id === activePatientId)?.name}'s vaccination history.</p>
                        <Input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="Enter email address"
                          autoComplete="off"
                          onChange={e => onChangeEmail(e.target.value)}
                          style={{ borderRadius: '25px' }}
                        />
                        {errors.email && (
                          <p className="text-danger" style={{ fontSize: '0.75rem' }}>{errors.email}</p>
                        )}
                        <div className='d-flex justify-content-end'>
                          <Button
                            className="mt-2 mb-1 mr-1"
                            color="primary"
                            outline
                            onClick={() => setEmailModalOpen(false)}
                            style={{ borderRadius: '25px', fontFamily: 'Spartan', fontWeight: 'bold' }}
                          >
                            Close
                          </Button>
                          <Button
                            className="mt-2 mb-1"
                            color="primary"
                            onClick={onSendEmail}
                            disabled={!email || loadingSendEmail}
                            style={{ borderRadius: '25px', fontFamily: 'Spartan', fontWeight: 'bold' }}
                          >
                            Send
                          </Button>
                        </div>
                      </ModalBody>
                    </Modal>
                    <Modal
                      isOpen={refillModalOpen}
                      className="modal-dialog-centered"
                      toggle={() => setRefillModalOpen(!refillModalOpen)}
                    >
                      <ModalHeader style={{ borderRadius: '25px' }}>
                        Request a Refill
                      </ModalHeader>
                      <ModalBody>
                        <p style={{ fontSize: '12px' }}>Press send below to notify us that you'd like to refill {patients.find(p => p.id === activePatientId)?.name}'s {selectedMedication?.description}.<br/>Is there anything else you'd like to tell us?</p>
                        <Input
                          type="textarea"
                          name="email"
                          id="email"
                          placeholder="(optional)"
                          rows="5"
                          autoComplete="off"
                          onChange={e => setRefillMessage(e.target.value)}
                          style={{ borderRadius: '25px' }}
                        />
                        <div className='d-flex justify-content-end'>
                          <Button
                            className="mt-2 mb-1 mr-1"
                            color="primary"
                            outline
                            onClick={() => setRefillModalOpen(false)}
                            style={{ borderRadius: '25px', fontFamily: 'Spartan', fontWeight: 'bold' }}
                          >
                            Close
                          </Button>
                          <Button
                            className="mt-2 mb-1"
                            color="primary"
                            onClick={onRequestRefill}
                            disabled={loadingSendEmail}
                            style={{ borderRadius: '25px', fontFamily: 'Spartan', fontWeight: 'bold' }}
                          >
                            Send
                          </Button>
                        </div>
                      </ModalBody>
                    </Modal>
                  </div>
                </>
              )}
              {(!client || patients.length === 0) && (
                <>
                  {!!client && patients.length === 0 && (
                    <p className='mt-2'>You currently have no active pets at {shop?.name}. Please give us a call at <a href={`tel:${shop?.phone}`}>{formatPhoneNumber(shop?.phoneNumber)}</a>, or schedule an appointment using the button below.</p>
                  )}
                  <div className="d-flex justify-content-center align-items-center flex-column">
                    <Button
                      className="mt-2 mb-1"
                      color="primary"
                      outline
                      onClick={scheduleNow}
                      style={{ borderRadius: '25px', fontFamily: 'Spartan', fontWeight: 'bold' }}
                    >
                      schedule now <Calendar size={18} />
                    </Button>
                    <NavbarSms minWidth={'100px'} fontSize={'14px'} buttonText="text us" />
                  </div>
                </>
              )}
            </>
          ) : (
            <Spinner color="primary" />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default MyPets;
